//v3
// Ultra-dynamic gas configuration with transparent safeguard logic
export const getTxGasConfig = async (web3, chainId, account) => {
  const pendingBlock = await web3.eth.getBlock("pending");

  const gweiToWei = (gwei) =>
    web3.utils.toBN(web3.utils.toWei(gwei.toString(), "gwei"));

  // EIP-1559 Chains
  if (["1", "137", "8453", "42161", "43114"].includes(chainId)) {
    const baseFee = web3.utils.toBN(pendingBlock.baseFeePerGas);

    // Safeguards - only activate when necessary
    const safeguards = {
      1: { min: 0.001, max: 10, reason: "Ethereum typically 0.001-3 gwei" },
      137: {
        min: 30,
        max: 150,
        reason: "Polygon network requires minimum 30 gwei",
      },
      8453: { min: 0.001, max: 1, reason: "Base typically 0.001-0.1 gwei" },
      42161: { min: 0.01, max: 1, reason: "Arbitrum floor is 0.01 gwei" },
      43114: { min: 0.1, max: 5, reason: "Avalanche typically 0.1-2 gwei" },
    };

    let priorityFee;
    let feeSource = "unknown";

    // 🔥 TRY TO USE NETWORK DATA FIRST
    try {
      const feeHistory = await web3.eth.getFeeHistory(
        20,
        "pending",
        [25, 50, 75],
      );

      if (feeHistory.reward && feeHistory.reward.length > 0) {
        // Get median of 50th percentile
        const rewards = feeHistory.reward
          .map((r) => web3.utils.toBN(r[1] || "0"))
          .filter((r) => r.gt(web3.utils.toBN(0)));

        if (rewards.length > 0) {
          rewards.sort((a, b) => (a.lt(b) ? -1 : a.gt(b) ? 1 : 0));
          const medianReward = rewards[Math.floor(rewards.length / 2)];

          // Add 10% safety buffer
          const networkPriorityFee = medianReward
            .mul(web3.utils.toBN(110))
            .div(web3.utils.toBN(100));

          const networkFeeGwei = parseFloat(
            web3.utils.fromWei(networkPriorityFee, "gwei"),
          );
          const minGwei = safeguards[chainId].min;
          const maxGwei = safeguards[chainId].max;

          // Apply safeguards only if needed
          if (networkFeeGwei < minGwei) {
            priorityFee = gweiToWei(minGwei);
            feeSource = `minimum (network ${networkFeeGwei.toFixed(4)} < min ${minGwei})`;
          } else if (networkFeeGwei > maxGwei) {
            priorityFee = gweiToWei(maxGwei);
            feeSource = `capped (network ${networkFeeGwei.toFixed(4)} > max ${maxGwei})`;
          } else {
            priorityFee = networkPriorityFee;
            feeSource = `network (${networkFeeGwei.toFixed(4)} gwei median + 10%)`;
          }
        } else {
          throw new Error("No valid rewards in fee history");
        }
      } else {
        throw new Error("Fee history empty");
      }
    } catch (e) {
      // Fallback to minimum
      priorityFee = gweiToWei(safeguards[chainId].min);
      feeSource = `fallback minimum (${e.message})`;
      console.warn(
        `[Gas] Chain ${chainId} - API failed, using fallback:`,
        e.message,
      );
    }

    // Dynamic base fee multiplier
    const baseFeeGwei = parseFloat(web3.utils.fromWei(baseFee, "gwei"));
    let multiplier;

    if (chainId === "137") {
      multiplier = 200; // Polygon always 2x
    } else if (baseFeeGwei < 1) {
      multiplier = 300; // 3x for ultra-low fees
    } else if (baseFeeGwei < 10) {
      multiplier = 250; // 2.5x
    } else if (baseFeeGwei < 50) {
      multiplier = 200; // 2x
    } else {
      multiplier = 150; // 1.5x for high fees
    }

    const maxFeePerGas = baseFee
      .mul(web3.utils.toBN(multiplier))
      .div(web3.utils.toBN(100))
      .add(priorityFee);

    // Clear logging
    console.log(`┌─ [Gas Config] Chain ${chainId} ─────────────────────`);
    console.log(`│ Base Fee: ${baseFeeGwei.toFixed(6)} gwei`);
    console.log(
      `│ Priority Fee: ${web3.utils.fromWei(priorityFee, "gwei")} gwei`,
    );
    console.log(`│ Source: ${feeSource}`);
    console.log(`│ Max Fee: ${web3.utils.fromWei(maxFeePerGas, "gwei")} gwei`);
    console.log(`│ Multiplier: ${multiplier / 100}x`);
    if (feeSource.includes("minimum") || feeSource.includes("capped")) {
      console.log(`│ ⚠️  SAFEGUARD ACTIVE: ${safeguards[chainId].reason}`);
    } else {
      console.log(`│ ✅ Using live network data`);
    }
    console.log(`└────────────────────────────────────────────────────`);

    return {
      from: account,
      maxFeePerGas: maxFeePerGas.toString(),
      maxPriorityFeePerGas: priorityFee.toString(),
    };
  }

  // BNB Chain (Legacy)
  if (chainId === "56") {
    const gasPrice = web3.utils.toBN(await web3.eth.getGasPrice());
    const adjustedGasPrice = gasPrice
      .mul(web3.utils.toBN(110))
      .div(web3.utils.toBN(100));

    console.log(
      `[Gas] BNB Chain: ${web3.utils.fromWei(adjustedGasPrice, "gwei")} gwei (1.1x network)`,
    );

    return {
      from: account,
      gasPrice: adjustedGasPrice.toString(),
    };
  }

  // Unknown chains
  console.warn(`[Gas] Unknown chain ${chainId}, using basic gas price`);
  const gasPrice = await web3.eth.getGasPrice();
  return {
    from: account,
    gasPrice: Math.floor(parseInt(gasPrice) + 2000000000).toString(),
  };
};
